import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    IdMissionCloseButton,
    HideNativeIdMissionCloseButton,
    ErrorWrapper,
    ProcessingOverlay,
} from './IdMissionPopup.style';
import { makeObservable, observable, computed, action, runInAction } from 'mobx';
import { Common, useCommon } from 'src/domains/common/Common';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { Popup } from 'src/domains/players/webview/ui/Popup';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { IdMissionValidationLazy } from './components/IdMissionValidation/IdMissionValidationLazy';
import {
    IdentityVerificationVariantType,
    IdentityEkycVerificationResultType,
} from 'src/api_openapi/v2/idMission/schemas';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { IdentityAPI } from 'src/domains/players/state/IdMission/IdentityAPI';
import { PersonalData } from 'idmission-web-sdk/dist/components/submission/types';
import { CloseIconIdMission } from 'src/domains/players/state/IdMission/IdMissionPopup/assets/CloseIconIdMission';
import { EntranceOverlay } from './components/EntranceOverlay/EntranceOverlay';
import { SuccessOverlay } from './components/SuccessOverlay/SuccessOverlay';
import { FailureOverlay } from './components/FailureOverlay/FailureOverlay';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';
import { Session } from 'src_common/sdk/session';
import {
    Heading,
    Header,
    Text,
    ButtonsGroup,
    Container,
} from 'src/domains/players/webview/components/Account/Notifications/NonBlockingNotifications.style';

export type IdmissionSDKType = IdentityVerificationVariantType;

export interface ErrorType {
    type: 'known' | 'unknown';
    message: string;
}

type ApiResultOverlayType = 'success' | 'failure';

export type OverlayStateType = 'none' | 'entrance' | ApiResultOverlayType;

const apiResultToOverlay = {
    success: 'success',
    failure: 'failure',
    'not-checked': 'failure',
    cancelled: 'failure',
} as const satisfies Record<IdentityEkycVerificationResultType, ApiResultOverlayType>;

export class IdMissionState {
    @observable public isIframeOpen: boolean = false;
    @observable public overlayState: OverlayStateType = 'none';
    @observable public isFadingOut: boolean = false;
    @observable public failedReason: string = 'an error';
    @observable public errors: ErrorType[] = [];
    @observable public showProcessingOverlay: boolean = false;
    @observable public hidePopupContent: boolean = false;
    public readonly sessionIdResource: Resource<string | null>;
    private readonly identityAPI: IdentityAPI;

    public constructor(
        private common: Common,
        private appPlayersState: AppPlayersState
    ) {
        makeObservable(this);
        this.identityAPI = IdentityAPI.get(common);
        this.sessionIdResource = new Resource(async (): Promise<string | null> => {
            try {
                const response = await this.identityAPI.getSessionId();
                if (response.status === 200) {
                    return response.body.id;
                } else {
                    this.createErrorMessage(`${response.status} - api error`);
                    return null;
                }
            } catch (error: unknown) {
                if (error instanceof Error) {
                    this.createErrorMessage(error.message);
                } else {
                    this.createErrorMessage(String(error));
                }
                return null;
            }
        });
    }

    @action public handleIframeToggle = (): void => {
        if (!this.isIframeOpen) {
            this.overlayState = 'entrance';
            this.isFadingOut = false;
            this.isIframeOpen = true;
            this.showProcessingOverlay = true;
            this.hidePopupContent = true;

            setTimeout(() => {
                runInAction(() => {
                    this.isFadingOut = true;
                });
            }, 2700);

            setTimeout(() => {
                runInAction(() => {
                    if (this.overlayState === 'entrance') {
                        this.overlayState = 'none';
                    }
                    this.isFadingOut = false;
                });
            }, 3000);
        } else {
            this.isIframeOpen = false;
            this.showProcessingOverlay = false;
        }
    };

    @action public handleCloseOverlay = async (): Promise<void> => {
        Session.blockLogout = false;

        const wasSuccess = this.overlayState === 'success';
        const wasFailure = this.overlayState === 'failure';

        this.overlayState = 'none';
        this.failedReason = 'an error';
        this.isIframeOpen = false;
        this.showProcessingOverlay = false;
        this.hidePopupContent = false;

        try {
            if (wasSuccess && this.common.session.isAuthorized) {
                const basicData = BasicDataModel.get(this.common).basicDataReady;
                const allNotifications = basicData?.prompts?.notificationList ?? [];
                const kycNotification = allNotifications.find((n) => n.name === 'kycDepositRestriction');

                if (kycNotification?.active === true) {
                    await this.appPlayersState.blockingNotificationsState.handleConfirmKycDepositRestrictionNotification();
                }
            }
        } catch (error) {
            console.error('handleCloseOverlay error:', error);
        }

        this.appPlayersState.identityState.closeIdMissionPopup();
        this.appPlayersState.identityState.refreshIdMissionData();

        if (wasFailure) {
            window.location.reload();
        }
    };

    @action public handleFinalize = async (id: number, response: unknown, accountId: number | null): Promise<void> => {
        Session.blockLogout = true;
        try {
            if (accountId === null) {
                runInAction(() => {
                    this.failedReason = 'Unknown accountId';
                    this.overlayState = 'failure';
                });
                return;
            }

            const finalizeResponse = await this.identityAPI.postFinalizeRequest({
                id,
                requestBody: {
                    universe: this.common.envVariables.universe,
                    accountId,
                    rawIdmidssionResult: JSON.stringify(response),
                },
            });

            if (finalizeResponse.status === 200) {
                const { result, description } = finalizeResponse.body;
                const overlayType = apiResultToOverlay[result];

                runInAction(() => {
                    if (overlayType === 'failure') {
                        this.failedReason = description;
                    }
                    this.overlayState = overlayType;
                });
            } else {
                runInAction(() => {
                    this.failedReason = "Sorry, we're experiencing an internal issue. Please try again later.";
                    this.overlayState = 'failure';
                });
            }
        } catch (error) {
            console.error('handleFinalize error:', error);
            runInAction(() => {
                this.failedReason = error instanceof Error ? error.message : 'Unknown error';
                this.overlayState = 'failure';
            });
        } finally {
            this.showProcessingOverlay = false;
        }
    };

    @computed public get transformCustomerData(): PersonalData | null {
        const customerData = BasicDataModel.get(this.common).basicDataReady;
        if (customerData === null) {
            return null;
        }

        return {
            addressLine1: customerData.addressLine1 ?? undefined,
            addressLine2: customerData.addressLine2 ?? undefined,
            city: customerData.city ?? undefined,
            country: customerData.country ?? undefined,
            email: customerData.email ?? undefined,
            name: `${customerData.firstName ?? ''} ${customerData.surname ?? ''}`,
            postalCode: customerData.postCode ?? '',
            phone: customerData.mobilePhone?.number,
            dob: customerData.birthDate.split('-').reverse().join('/'),
        };
    }

    @action public createErrorMessage = (message?: string): void => {
        const error: ErrorType =
            message === undefined ? { type: 'unknown', message: 'Unknown Error' } : { type: 'known', message };
        this.errors.push(error);
    };
}

export const IdMission = observer('IdMission', () => {
    const common = useCommon();
    const appPlayersState = AppPlayersState.get(common);
    const [state] = useState(() => new IdMissionState(common, appPlayersState));

    const sessionId = state.sessionIdResource.getReady();
    const isOpen = appPlayersState.identityState.processIdMission.isOpen;
    const id = appPlayersState.identityState.processIdMission.id ?? null;
    const isKycDepositRestriction = appPlayersState.identityState.shouldShowSecondThresholdVariant;

    const hasActiveOverlay = state.overlayState === 'success' || state.overlayState === 'failure';
    const hasActiveProcess = state.isIframeOpen;

    if ((isOpen === false || id === null) && !hasActiveOverlay && !hasActiveProcess) {
        return null;
    }

    const renderIframeAndOverlays = (): JSX.Element | null => {
        if (!((sessionId !== null && state.isIframeOpen) || hasActiveOverlay)) {
            return null;
        }

        const isProcessing = !hasActiveOverlay;

        return (
            <div data-test='id-mission-iframe-container'>
                {state.showProcessingOverlay && isProcessing && (
                    <ProcessingOverlay data-test='id-mission-processing-overlay' />
                )}
                {sessionId !== null && id !== null && isProcessing && (
                    <>
                        <IdMissionCloseButton
                            onClick={(): void => {
                                state.handleIframeToggle();
                            }}
                            data-test='id-mission-iframe-close-button'
                        >
                            <CloseIconIdMission />
                        </IdMissionCloseButton>
                        <HideNativeIdMissionCloseButton>
                            <IdMissionValidationLazy
                                sessionId={sessionId}
                                openType={appPlayersState.identityState.processIdMission.type ?? 'IdCard'}
                                handleToggle={state.handleIframeToggle}
                                personalData={state.transformCustomerData}
                                handleFinalize={state.handleFinalize}
                                id={id}
                            />
                        </HideNativeIdMissionCloseButton>
                    </>
                )}
                {((): JSX.Element | null => {
                    switch (state.overlayState) {
                        case 'entrance':
                            return <EntranceOverlay isFadingOut={state.isFadingOut} />;
                        case 'success':
                            return <SuccessOverlay onClose={state.handleCloseOverlay} />;
                        case 'failure':
                            return (
                                <FailureOverlay
                                    onClose={state.handleCloseOverlay}
                                    failedReason={state.failedReason}
                                />
                            );
                        default:
                            return null;
                    }
                })()}
            </div>
        );
    };

    if (state.hidePopupContent && (hasActiveProcess || hasActiveOverlay)) {
        return renderIframeAndOverlays();
    }

    return (
        <Popup
            className='idmission-popup'
            overlayClose={true}
            closeButton={true}
            maxWidth='320px'
            colorOverlay={isKycDepositRestriction}
            onClose={(): void => {
                appPlayersState.identityState.closeIdMissionPopup();
            }}
        >
            <Container>
                <Header>
                    <Heading data-test='id-mission.popup.header'>
                        <I18n
                            langKey='id-mission.popup.title'
                            defaultText='Action Needed: Verify Your Account '
                        />
                        <Text data-test='id-mission-popup.text'>
                            {isKycDepositRestriction ? (
                                <I18n
                                    langKey='id-mission.popup.text.threshold-2'
                                    defaultText='Your deposits are temporarily restricted, but you can still place bets. Please verify directly on our website with secure ID verification, or check your email for document submission instructions. Once verified, deposit restrictions will be lifted!'
                                />
                            ) : (
                                <I18n
                                    langKey='id-mission.popup.text.threshold-1'
                                    defaultText='To continue using your account, please verify directly on our website with secure ID verification, or check your email for document submission instructions.'
                                />
                            )}
                        </Text>
                    </Heading>
                </Header>
                <ButtonsGroup>
                    <Button
                        size='medium'
                        onClick={state.handleIframeToggle}
                    >
                        <I18n
                            langKey='id-mission.popup.button.verify'
                            defaultText='Verify Now'
                        />
                    </Button>
                    <Button
                        size='medium'
                        version='secondary-light'
                        onClick={(): void => {
                            appPlayersState.identityState.closeIdMissionPopup();
                        }}
                    >
                        <I18n
                            langKey='id-mission.popup.button.remind'
                            defaultText='Remind me later'
                        />
                    </Button>
                </ButtonsGroup>

                {state.errors.length > 0 && (
                    <ErrorWrapper>
                        {state.errors.map((error, index) => (
                            <Messages
                                type='error'
                                marginTop='8px'
                                key={`${error.type}-${index}`}
                                message={error.message}
                            />
                        ))}
                    </ErrorWrapper>
                )}

                {renderIframeAndOverlays()}
            </Container>
        </Popup>
    );
});
